import React, { useEffect, useState, Suspense, lazy, createElement, Component } from 'react';
import { logger } from './utils/logger';
import { checkLowStockAlerts } from './utils/stockAlertService';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ui/ToastContainer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DataProvider } from './contexts/DataContext';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { PatientSelfRegisterPage } from './pages/PatientSelfRegisterPage';
import { getToday } from './utils/helpers';
import { startAutoBackupScheduler, stopAutoBackupScheduler } from './utils/backupService';
// Lazy-loaded modules to avoid compilation timeout
const Dashboard = lazy(() => import('./components/modules/Dashboard').then(m => ({
  default: m.Dashboard
})));
const Patients = lazy(() => import('./components/modules/Patients').then(m => ({
  default: m.Patients
})));
const Patient360 = lazy(() => import('./components/modules/Patient360').then(m => ({
  default: m.Patient360
})));
const SessionPackages = lazy(() => import('./components/modules/SessionPackages').then(m => ({
  default: m.SessionPackages
})));
const GiftCertificates = lazy(() => import('./components/modules/GiftCertificates').then(m => ({
  default: m.GiftCertificates
})));
const Loyalty = lazy(() => import('./components/modules/Loyalty').then(m => ({
  default: m.Loyalty
})));
const Doctors = lazy(() => import('./components/modules/Doctors').then(m => ({
  default: m.Doctors
})));
const Appointments = lazy(() => import('./components/modules/Appointments').then(m => ({
  default: m.Appointments
})));
const Services = lazy(() => import('./components/modules/Services').then(m => ({
  default: m.Services
})));
const Offers = lazy(() => import('./components/modules/Offers').then(m => ({
  default: m.Offers
})));
const Insurance = lazy(() => import('./components/modules/Insurance').then(m => ({
  default: m.Insurance
})));
const Specialties = lazy(() => import('./components/modules/Specialties').then(m => ({
  default: m.Specialties
})));
const Contacts = lazy(() => import('./components/modules/Contacts').then(m => ({
  default: m.Contacts
})));
const Opportunities = lazy(() => import('./components/modules/Opportunities').then(m => ({
  default: m.Opportunities
})));
const Settings = lazy(() => import('./components/modules/Settings').then(m => ({
  default: m.Settings
})));
const Products = lazy(() => import('./components/modules/Products').then(m => ({
  default: m.Products
})));
const Intelligence = lazy(() => import('./components/modules/Intelligence').then(m => ({
  default: m.Intelligence
})));
const Commissions = lazy(() => import('./components/modules/Commissions').then(m => ({
  default: m.Commissions
})));
const ClinicalHistory = lazy(() => import('./components/modules/ClinicalHistory').then(m => ({
  default: m.ClinicalHistory
})));
const AuditLog = lazy(() => import('./components/modules/AuditLog').then(m => ({
  default: m.AuditLog
})));
const FollowUpNotes = lazy(() => import('./components/modules/FollowUpNotes').then(m => ({
  default: m.FollowUpNotes
})));
const BotOmnichannel = lazy(() => import('./components/modules/BotOmnichannel').then(m => ({
  default: m.BotOmnichannel
})));
const Goals = lazy(() => import('./components/modules/Goals').then(m => ({
  default: m.Goals
})));
// ==================== ERROR BOUNDARY ====================
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: 'full' | 'inline' | 'hidden';
  name?: string;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const name = this.props.name || 'Unknown';
    logger.error(`[ErrorBoundary:${name}] caught:`, error.message);
    if (errorInfo.componentStack) {
      logger.error(`[ErrorBoundary:${name}] stack:`, errorInfo.componentStack);
    }
    // Auto-clear corrupted localStorage for #310 errors
    if (error.message && (error.message.includes('310') || error.message.includes('Objects are not valid'))) {
      logger.warn(`[ErrorBoundary:${name}] Error #310 detected - clearing potentially corrupted data`);
      const keysToClean = ['savidna-notifications', 'savidna-audit-logs', 'savidna-follow-up-notes', 'savidna-audit-log-status', 'savidna-followup-notes-status', 'savidna-patients-status', 'savidna-doctors-status', 'savidna-services-status', 'savidna-specialties-status', 'savidna-insurance-status', 'savidna-contacts-status', 'savidna-companies-status', 'savidna-opportunities-status', 'savidna-clinical-history-status'];
      keysToClean.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch {}
      });
    }
  }
  render() {
    if (this.state.hasError) {
      const fallback = this.props.fallback || 'full';
      const name = this.props.name || '';
      // Hidden: silently swallow the error (for non-critical sections)
      if (fallback === 'hidden') return null;
      // Inline: show a small error message within the page
      if (fallback === 'inline') {
        return createElement('div', {
          style: {
            padding: '24px',
            margin: '16px 0',
            background: '#fef2f2',
            borderRadius: '12px',
            border: '1px solid #fecaca',
            textAlign: 'center' as const,
            fontFamily: 'system-ui, sans-serif'
          }
        }, createElement('p', {
          style: {
            color: '#991b1b',
            fontWeight: '600',
            fontSize: '14px',
            marginBottom: '8px'
          }
        }, `Error al cargar ${name || 'esta sección'}`), createElement('p', {
          style: {
            color: '#b91c1c',
            fontSize: '12px',
            marginBottom: '12px',
            fontFamily: 'monospace',
            background: '#fee2e2',
            padding: '8px 12px',
            borderRadius: '6px',
            textAlign: 'left' as const,
            wordBreak: 'break-all' as const,
            maxHeight: '120px',
            overflow: 'auto'
          }
        }, String(this.state.error?.message || 'Error desconocido')), createElement('button', {
          onClick: () => this.setState({
            hasError: false,
            error: null
          }),
          style: {
            padding: '6px 16px',
            borderRadius: '8px',
            border: '1px solid #fca5a5',
            background: 'white',
            color: '#dc2626',
            fontWeight: '500',
            cursor: 'pointer',
            fontSize: '13px'
          }
        }, 'Reintentar'));
      }
      // Full: show the full error page (for the outermost boundary)
      return createElement('div', {
        style: {
          padding: '40px',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: '600px',
          margin: '80px auto',
          textAlign: 'center' as const
        }
      }, createElement('div', {
        style: {
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }
      }, createElement('svg', {
        width: '32',
        height: '32',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'white',
        strokeWidth: '2',
        strokeLinecap: 'round',
        strokeLinejoin: 'round'
      }, createElement('path', {
        d: 'M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5'
      }))), createElement('h1', {
        style: {
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#111827',
          marginBottom: '8px'
        }
      }, 'Savidna - Error Temporal'), createElement('p', {
        style: {
          color: '#6b7280',
          marginBottom: '24px',
          lineHeight: '1.6'
        }
      }, 'Ocurrió un error al cargar la aplicación. Esto puede deberse a datos en caché.'), createElement('div', {
        style: {
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap' as const
        }
      }, createElement('button', {
        onClick: () => this.setState({
          hasError: false,
          error: null
        }),
        style: {
          padding: '10px 24px',
          borderRadius: '10px',
          border: 'none',
          background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
          color: 'white',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '14px'
        }
      }, 'Reintentar'), createElement('button', {
        onClick: () => {
          // Clear ALL savidna localStorage except theme
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('savidna-') && key !== 'savidna-theme') {
              localStorage.removeItem(key);
            }
          });
          window.location.reload();
        },
        style: {
          padding: '10px 24px',
          borderRadius: '10px',
          border: '1px solid #d1d5db',
          background: 'white',
          color: '#374151',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '14px'
        }
      }, 'Limpiar Caché y Recargar')), createElement('details', {
        style: {
          marginTop: '24px',
          textAlign: 'left' as const,
          background: '#f9fafb',
          borderRadius: '8px',
          padding: '16px'
        }
      }, createElement('summary', {
        style: {
          cursor: 'pointer',
          color: '#6b7280',
          fontSize: '13px',
          fontWeight: '500'
        }
      }, 'Detalles técnicos'), createElement('pre', {
        style: {
          marginTop: '8px',
          fontSize: '12px',
          color: '#ef4444',
          whiteSpace: 'pre-wrap' as const,
          wordBreak: 'break-all' as const
        }
      }, String(this.state.error?.message || '') + '\n\n' + String(this.state.error?.stack || ''))));
    }
    return this.props.children;
  }
}
// ==================== SAFE MODULE WRAPPER ====================
// Wraps each module in its own error boundary so one broken module
// can never crash the entire application
function SafeModule({
  name,
  children
}: {
  name: string;
  children: React.ReactNode;
}) {
  return <ErrorBoundary fallback="inline" name={name} data-id="element-0" data-mp="2tlee7z658r32">
      {children}
    </ErrorBoundary>;
}
// ==================== SESSION EXPIRED OVERLAY ====================
function SessionExpiredOverlay() {
  const {
    sessionExpired,
    sessionWarning,
    sessionSecondsLeft,
    logout,
    extendCurrentSession
  } = useAuth();
  // Format seconds as mm:ss
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  // Warning overlay - session about to expire
  if (sessionWarning && !sessionExpired) {
    return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-1" data-mp="1fhlzw2ao0nw0">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-id="element-2" data-mp="mqlv3p4ifg3a">
          <div className="h-1.5 bg-gradient-to-r from-orange-400 to-amber-500" data-id="element-3" data-mp="11nlcg4p5ynay" />
          <div className="p-8 text-center" data-id="element-4" data-mp="11lid2u24fhtk">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center" data-id="element-5" data-mp="wnqhnq29pdkm">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500 dark:text-orange-400" data-id="element-6" data-mp="2d54jy99s1cfb">
                
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-id="element-7" data-mp="vmkzdxb29iym" />
                <line x1="12" y1="9" x2="12" y2="13" data-id="element-8" data-mp="10lxo6nlht03u" />
                <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-9" data-mp="10lfhex17f3ho" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-10" data-mp="15ixsasys7ssb">
              ¿Sigues ahí?
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-3 leading-relaxed" data-id="element-11" data-mp="1adv1bzw2a257">
              Tu sesión está a punto de expirar por inactividad.
            </p>
            <div className="text-4xl font-bold text-orange-500 dark:text-orange-400 mb-6 tabular-nums" data-id="element-12" data-mp="1jdqe2959m57c">
              {formatTime(sessionSecondsLeft)}
            </div>
            <div className="flex gap-3" data-id="element-13" data-mp="1yfgqr38gr090">
              <button onClick={() => {
              try {
                localStorage.removeItem('savidna-session');
                localStorage.removeItem('savidna-user-id');
                sessionStorage.clear();
              } catch {}
              window.location.href = window.location.origin;
            }} className="flex-1 px-4 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200" data-id="element-14" data-mp="1ygi5bhoou4vh">
                
                Cerrar Sesión
              </button>
              <button onClick={extendCurrentSession} className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20 transition-all duration-200" data-id="element-15" data-mp="23ejkvsmwnm27">
                
                Continuar
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Expired overlay
  if (!sessionExpired) return null;
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-id="element-16" data-mp="y8uu0xlz2ppl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-id="element-17" data-mp="oh78fdc3k2w1">
        <div className="h-1.5 bg-gradient-to-r from-amber-500 to-orange-500" data-id="element-18" data-mp="ahpigun2ejl3" />
        <div className="p-8 text-center" data-id="element-19" data-mp="ajscdprc8wv3">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center" data-id="element-20" data-mp="fhk5gn3pyp0v">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 dark:text-amber-400" data-id="element-21" data-mp="l2u3vvj5aeb">
              
              <circle cx="12" cy="12" r="10" data-id="element-22" data-mp="3key40c273yqi" />
              <polyline points="12 6 12 12 16 14" data-id="element-23" data-mp="3fflfznzl8lmc" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-24" data-mp="3u47slhxtyz9v">
            Sesión Expirada
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed" data-id="element-25" data-mp="3pczisksl9bw6">
            Tu sesión expiró por inactividad. Por favor, inicia sesión
            nuevamente para continuar.
          </p>
          <button onClick={() => {
          try {
            localStorage.removeItem('savidna-session');
            localStorage.removeItem('savidna-user-id');
            sessionStorage.clear();
          } catch {}
          window.location.href = window.location.origin;
        }} className="w-full px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20 transition-all duration-200" data-id="element-26" data-mp="2yoiukfdxa30">
            
            Iniciar Sesión de Nuevo
          </button>
        </div>
      </div>
    </div>;
}
// ==================== APP CONTENT ====================
function AppContent() {
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const [currentModule, setCurrentModule] = useState('dashboard');
  const [moduleParams, setModuleParams] = useState<Record<string, string>>({});
  // Wrapper to handle module navigation with optional params (e.g., 'appointments:today')
  const handleNavigate = (moduleStr: string) => {
    if (moduleStr.includes(':')) {
      const [mod, param] = moduleStr.split(':');
      setCurrentModule(mod);
      setModuleParams({
        filter: param,
        action: param
      });
    } else {
      setCurrentModule(moduleStr);
      setModuleParams({});
    }
  };
  // ── Placeholder color fix for dark mode ──────────────────────────────────
  useEffect(() => {
    const styleId = 'savidna-placeholder-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .dark input::placeholder,
        .dark textarea::placeholder,
        .dark input::-webkit-input-placeholder,
        .dark textarea::-webkit-input-placeholder,
        .dark input::-moz-placeholder,
        .dark textarea::-moz-placeholder {
          color: #94a3b8 !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  // Dynamic page title
  useEffect(() => {
    const moduleNames: Record<string, string> = {
      dashboard: 'Panel de Control',
      patients: 'Pacientes',
      patient360: 'Cliente 360',
      doctors: 'Doctores',
      appointments: 'Citas',
      'clinical-history': 'Historial Clínico',
      goals: 'Metas y Alertas',
      services: 'Servicios',
      'gift-certificates': 'Certificados de Regalo',
      loyalty: 'Tarjetas de Fidelidad',
      offers: 'Ofertas',
      insurance: 'Seguros',
      specialties: 'Especialidades',
      contacts: 'Contactos',
      opportunities: 'Oportunidades',
      'follow-up-notes': 'Reportes diarios',
      'audit-log': 'Bitácora',
      products: 'Productos',
      intelligence: 'Inteligencia',
      commissions: 'Comisiones',
      'bot-omnichannel': 'Bot Omnichannel',
      settings: 'Configuración'
    };
    const moduleName = moduleNames[currentModule] || 'Panel de Control';
    document.title = `Savidna CRM — ${moduleName}`;
  }, [currentModule]);
  useEffect(() => {
    if (isAuthenticated) {
      setCurrentModule('dashboard');
      setModuleParams({});
    }
  }, [isAuthenticated]);
  useEffect(() => {
    ;
    (window as any).addNotification = addNotification;
    return () => {
      delete (window as any).addNotification;
    };
  }, [addNotification]);
  // Run stock alert check ONCE per login session (not per module mount)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const SESSION_STOCK_CHECK = 'savidna-stock-login-check';
    if (sessionStorage.getItem(SESSION_STOCK_CHECK)) return;
    sessionStorage.setItem(SESSION_STOCK_CHECK, '1');
    // Wait for DataContext to load products before checking
    const timer = setTimeout(async () => {
      try {
        const {
          supabase
        } = await import('./utils/supabase');
        const {
          data
        } = await supabase.from('products').select('id,name,stock,min_stock,sku').eq('is_active', true);
        if (data && data.length > 0) {
          const products = data.map((p: any) => ({
            id: p.id,
            name: p.name,
            stock: p.stock ?? 0,
            minStock: p.min_stock ?? 5,
            sku: p.sku
          }));
          checkLowStockAlerts(products, addNotification);
        }
      } catch (e) {
        logger.error('Stock check on login failed:', e);
      }
    }, 4000); // 4s delay to let app settle after login
    return () => clearTimeout(timer);
  }, [isAuthenticated, user?.id]);
  // Request desktop notification permission once on login
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    import('./utils/desktopNotifications').then(({
      requestDesktopPermissionOnce
    }) => {
      requestDesktopPermissionOnce().then(status => {
        logger.log('[App] Permiso de notificaciones de escritorio:', status);
      });
    });
  }, [isAuthenticated, user]);
  // Auto-backup scheduler — singleton, solo para administradores
  useEffect(() => {
    if (!isAuthenticated || !user) {
      stopAutoBackupScheduler();
      return;
    }
    if (user.role === 'administrador') {
      startAutoBackupScheduler(user.id, user.name);
    }
    return () => {
      stopAutoBackupScheduler();
    };
  }, [isAuthenticated, user?.id, user?.role]);
  if (!isAuthenticated) {
    return <LoginPage data-id="element-27" data-mp="2rpf376nhlada" />;
  }
  // Patient self-registration role - show only registration page
  if (user?.role === 'paciente') {
    return <PatientSelfRegisterPage data-id="element-28" data-mp="1774ivaq99ea0" />;
  }
  const renderModule = () => {
    switch (currentModule) {
      case 'dashboard':
        return <SafeModule name="Dashboard" data-id="element-29" data-mp="xajemrm28778">
            <Suspense fallback={<ModuleLoader data-id="element-31" data-mp="xc2rs9cf624s" />} data-id="element-30" data-mp="xbkst4ydh9t6">
              <Dashboard onNavigate={handleNavigate} data-id="element-32" data-mp="12930yt4kfl04" />
            </Suspense>
          </SafeModule>;
      case 'patients':
        return <SafeModule name="Pacientes" data-id="element-33" data-mp="1q08ev2qy437s">
            <Suspense fallback={<ModuleLoader data-id="element-35" data-mp="1uxrti3rk2i09" />} data-id="element-34" data-mp="1px46ruad0nbu">
              <Patients autoOpenNew={moduleParams.action === 'new'} data-id="element-36" data-mp="1uwp4609vsnmg" />
            </Suspense>
          </SafeModule>;
      case 'patient360':
        return <SafeModule name="Cliente 360" data-id="element-37" data-mp="1kzkf9sonie1y">
            <Suspense fallback={<ModuleLoader data-id="element-39" data-mp="1ky0zs4atk718" />} data-id="element-38" data-mp="1kyj1vhk5xfh2">
              <Patient360 data-id="element-40" data-mp="5j3j282iza4v" />
            </Suspense>
          </SafeModule>;
      case 'session-packages':
        return <SafeModule name="Paquetes de Sesiones" data-id="element-41" data-mp="9kxgm7hfby0t">
            <Suspense fallback={<ModuleLoader data-id="element-43" data-mp="prtmbks3g91h" />} data-id="element-42" data-mp="9ikq35xlofc5">
              <SessionPackages data-id="element-44" data-mp="ps3ad2tkw64j" />
            </Suspense>
          </SafeModule>;
      case 'gift-certificates':
        return <SafeModule name="Certificados de Regalo" data-id="element-45" data-mp="kzinymd04vb1">
            <Suspense fallback={<ModuleLoader data-id="element-47" data-mp="g31ynou2gawd" />} data-id="element-46" data-mp="g1h25to6yhvv">
              <GiftCertificates data-id="element-48" data-mp="g29zy5569x1v" />
            </Suspense>
          </SafeModule>;
      case 'loyalty':
        return <SafeModule name="Tarjetas de Fidelidad" data-id="element-49" data-mp="b12tqaioh5xb">
            <Suspense fallback={<ModuleLoader data-id="element-51" data-mp="18pwg0cvl6u5m" />} data-id="element-50" data-mp="18pcwwsu79zq0">
              <Loyalty data-id="element-52" data-mp="13rl5s11dqhmv" />
            </Suspense>
          </SafeModule>;
      case 'doctors':
        return <SafeModule name="Doctores" data-id="element-53" data-mp="ys89yqsj6085">
            <Suspense fallback={<ModuleLoader data-id="element-55" data-mp="yqotaz2ypn5u" />} data-id="element-54" data-mp="yr6tu7wnqnjk">
              <Doctors data-id="element-56" data-mp="uqwomxgow15r" />
            </Suspense>
          </SafeModule>;
      case 'appointments':
        return <SafeModule name="Citas" data-id="element-57" data-mp="3jj8rf0ay0nqd">
            <Suspense fallback={<ModuleLoader data-id="element-59" data-mp="3jgmjv0o1y885" />} data-id="element-58" data-mp="3jkabtvdummkr">
              <Appointments initialDateFilter={moduleParams.filter === 'today' ? getToday() : undefined} autoOpenNew={moduleParams.action === 'new'} data-id="element-60" data-mp="3fhdo5q9gc09y" />
              
            </Suspense>
          </SafeModule>;
      case 'clinical-history':
        return <SafeModule name="Historial Clínico" data-id="element-61" data-mp="23yq8qq45rs4h">
            <Suspense fallback={<ModuleLoader data-id="element-63" data-mp="28q80qoo7eidb" />} data-id="element-62" data-mp="23xyb79r03kbk">
              <ClinicalHistory data-id="element-64" data-mp="28rjbj8g2gqdx" />
            </Suspense>
          </SafeModule>;
      case 'goals':
        return <SafeModule name="Metas y Alertas" data-id="element-65" data-mp="2dp2pe78ur15c">
            <Suspense fallback={<ModuleLoader data-id="element-67" data-mp="2inw586bm348o" />} data-id="element-66" data-mp="2iofeyzr3ymbm">
              <Goals data-id="element-68" data-mp="2in4aexpu9ajg" />
            </Suspense>
          </SafeModule>;
      case 'services':
        return <SafeModule name="Servicios" data-id="element-69" data-mp="2mr2d1iedh3h9">
            <Suspense fallback={<ModuleLoader data-id="element-71" data-mp="2roau1fnsb1gx" />} data-id="element-70" data-mp="2rpvoz6e0gmfb">
              <Services data-id="element-72" data-mp="2rokl76kkfeo7" />
            </Suspense>
          </SafeModule>;
      case 'offers':
        return <SafeModule name="Ofertas" data-id="element-73" data-mp="sechkw8cvm7r">
            <Suspense fallback={<ModuleLoader data-id="element-75" data-mp="xcdvz3uu6xcw" />} data-id="element-74" data-mp="xat45lzwpsiq">
              <Offers data-id="element-76" data-mp="xbkwmny7ja4s" />
            </Suspense>
          </SafeModule>;
      case 'insurance':
        return <SafeModule name="Seguros" data-id="element-77" data-mp="20o37ir56zl59">
            <Suspense fallback={<ModuleLoader data-id="element-79" data-mp="1vogtifhji0ve" />} data-id="element-78" data-mp="1vnxaevg5l6fs">
              <Insurance data-id="element-80" data-mp="1vp8fqzp8ym90" />
            </Suspense>
          </SafeModule>;
      case 'specialties':
        return <SafeModule name="Especialidades" data-id="element-81" data-mp="24nts2d44l51c">
            <Suspense fallback={<ModuleLoader data-id="element-83" data-mp="24mafawhohc4b" />} data-id="element-82" data-mp="24mskiimbj0od">
              <Specialties data-id="element-84" data-mp="2jl4euim7xxcd" />
            </Suspense>
          </SafeModule>;
      case 'contacts':
        return <SafeModule name="Contactos" data-id="element-85" data-mp="2em9nute3l2h6">
            <Suspense fallback={<ModuleLoader data-id="element-87" data-mp="2ejx6ahc94u4n" />} data-id="element-86" data-mp="2eli1882haf31">
              <Contacts data-id="element-88" data-mp="2sd6a82hfknfs" />
            </Suspense>
          </SafeModule>;
      case 'opportunities':
        return <SafeModule name="Oportunidades" data-id="element-89" data-mp="2obkpxw2kh6vk">
            <Suspense fallback={<ModuleLoader data-id="element-91" data-mp="2oe70m4kr82i0" />} data-id="element-90" data-mp="2ocmbwvl4fds2">
              <Opportunities data-id="element-92" data-mp="32asu73dl756m" />
            </Suspense>
          </SafeModule>;
      case 'follow-up-notes':
        return <SafeModule name="Reportes diarios" data-id="element-93" data-mp="2xdu3yvx1nidp">
            <Suspense fallback={<ModuleLoader data-id="element-95" data-mp="3gxrl8x8j7xkt" />} data-id="element-94" data-mp="3gyazo3yz43u3">
              <FollowUpNotes data-id="element-96" data-mp="3gyu88u2qhfvg" />
            </Suspense>
          </SafeModule>;
      case 'audit-log':
        return <SafeModule name="Bitácora" data-id="element-97" data-mp="3lvb82ljapiod">
            <Suspense fallback={<ModuleLoader data-id="element-99" data-mp="36z3cyq6cmwub" />} data-id="element-98" data-mp="370mu0iztxbx5">
              <AuditLog data-id="element-100" data-mp="36zvaw7nf56nr" />
            </Suspense>
          </SafeModule>;
      case 'products':
        return <SafeModule name="Productos" data-id="element-101" data-mp="3c1as1omh6t0v">
            <Suspense fallback={<ModuleLoader data-id="element-103" data-mp="2y71euyjr5sn7" />} data-id="element-102" data-mp="2y7jk2koe7h79">
              <Products data-id="element-104" data-mp="2y5qb2yqb4kw3" />
            </Suspense>
          </SafeModule>;
      case 'intelligence':
        return <SafeModule name="Inteligencia" data-id="element-105" data-mp="2o9nb9zixgycb">
            <Suspense fallback={<ModuleLoader data-id="element-107" data-mp="2ob6sptgblffo" />} data-id="element-106" data-mp="2oaoly2w17iti">
              <Intelligence data-id="element-108" data-mp="2o7sr4nfhda9a" />
            </Suspense>
          </SafeModule>;
      case 'commissions':
        return <SafeModule name="Comisiones" data-id="element-109" data-mp="2f8fczhgevxi8">
            <Suspense fallback={<ModuleLoader data-id="element-111" data-mp="2fb1vud8r5zfr" />} data-id="element-110" data-mp="2f9h2gqy6cmjh">
              <Commissions data-id="element-112" data-mp="2jaaofes23r9q" />
            </Suspense>
          </SafeModule>;
      case 'bot-omnichannel':
        return <SafeModule name="Bot Omnichannel" data-id="element-113" data-mp="2zp0cnksgflap">
            <Suspense fallback={<ModuleLoader data-id="element-115" data-mp="2uqyr8t7k39w2" />} data-id="element-114" data-mp="2znyxp58bienp">
              <BotOmnichannel data-id="element-116" data-mp="2us06l9vluijl" />
            </Suspense>
          </SafeModule>;
      case 'settings':
        return <SafeModule name="Configuración" data-id="element-117" data-mp="2pqasfld35ora">
            <Suspense fallback={<ModuleLoader data-id="element-119" data-mp="2ktanjdrz2s1r" />} data-id="element-118" data-mp="2ksst8movenwh">
              <Settings data-id="element-120" data-mp="2ks9aj3rebvhe" />
            </Suspense>
          </SafeModule>;
      default:
        return <SafeModule name="Dashboard" data-id="element-121" data-mp="2gsqp86vnxie1">
            <Suspense fallback={<ModuleLoader data-id="element-123" data-mp="2bzxodu3beu78" />} data-id="element-122" data-mp="2c0h18we3ysee">
              <Dashboard onNavigate={handleNavigate} data-id="element-124" data-mp="2byw9tfn3bpkr" />
            </Suspense>
          </SafeModule>;
    }
  };
  return <ErrorBoundary fallback="inline" name="Layout" data-id="element-125" data-mp="26z9w753co7bf">
      <SessionExpiredOverlay data-id="element-126" data-mp="2232pha34qrsf" />
      <Layout currentModule={currentModule} onModuleChange={handleNavigate} data-id="element-127" data-mp="223ulgm0n2rj8">
        {renderModule()}
      </Layout>
    </ErrorBoundary>;
}
// ==================== PROVIDERS WRAPPER ====================
// Each provider is wrapped in its own error boundary so a failure
// in one provider doesn't take down the entire app
function SafeProviders({
  children
}: {
  children: React.ReactNode;
}) {
  return <ToastProvider data-id="element-128" data-mp="cjcjpkzt6n68">
      <ErrorBoundary fallback="full" name="ThemeProvider" data-id="element-129" data-mp="cn0e0md2vdm0">
        <ThemeProvider data-id="element-130" data-mp="hlk2ouj233i2">
          <ErrorBoundary fallback="full" name="AuthProvider" data-id="element-131" data-mp="hl1ztf1w1r16">
            <AuthProvider data-id="element-132" data-mp="miag1a3h7kzs">
              <ErrorBoundary fallback="full" name="NotificationProvider" data-id="element-133" data-mp="mik7z3830284">
                <NotificationProvider data-id="element-134" data-mp="3l20o61cd41lg">
                  <ErrorBoundary fallback="full" name="DataProvider" data-id="element-135" data-mp="3kywckiwki3kr">
                    <DataProvider data-id="element-136" data-mp="3q0lm1u45696q">
                      <ConfirmProvider data-id="element-137" data-mp="3pxpr8enlc0mi">
                        {children}
                        <ToastContainer data-id="element-138" data-mp="3uxu7q4oi0vcq" />
                      </ConfirmProvider>
                    </DataProvider>
                  </ErrorBoundary>
                </NotificationProvider>
              </ErrorBoundary>
            </AuthProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </ErrorBoundary>
    </ToastProvider>;
}
// ==================== APP ROOT ====================
export function App() {
  return <ErrorBoundary fallback="full" name="App" data-id="element-139" data-mp="2gxu68sx1abv4">
      <SafeProviders data-id="element-140" data-mp="2lxoxixkq13gp">
        <AppContent data-id="element-141" data-mp="2lxyrew8vzuro" />
      </SafeProviders>
    </ErrorBoundary>;
}
// Lazy loading fallback spinner
function ModuleLoader() {
  return <div className="flex items-center justify-center py-20" data-id="element-142" data-mp="2c0ak76u3gusm">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" data-id="element-143" data-mp="2byr4bhccolrd"></div>
    </div>;
}